import React from 'react';
import { FunnelIcon, XMarkIcon, ChevronUpDownIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { ExploreFilters } from '../../types';

interface FilterBarProps {
  filters: ExploreFilters;
  onFilterChange: (key: keyof ExploreFilters, value: any) => void;
  onClearFilters: () => void;
  onShowAdvancedFilters: () => void;
  activeFiltersCount: number;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  onShowAdvancedFilters,
  activeFiltersCount
}) => {
  const userTypes = [
    { value: 'creator', label: 'Creators' },
    { value: 'model', label: 'Models' }
  ];

  const experienceLevels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'professional', label: 'Professional' }
  ];

  const [isSortOpen, setIsSortOpen] = React.useState(false);
  const sortRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 shadow-sm sticky top-[60px] md:top-[68px] z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 md:py-2">
        <div className="flex items-center gap-2 overflow-x-auto md:overflow-visible no-scrollbar w-full pb-1">
          {/* Filters Button */}
          <button
            onClick={onShowAdvancedFilters}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${
              activeFiltersCount > 0
                ? 'bg-black dark:bg-white border-black dark:border-white text-white dark:text-black'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <FunnelIcon className="w-4 h-4" />
            <span className="hidden md:inline">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="bg-white dark:bg-black text-black dark:text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ml-1 font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {/* Sort Dropdown */}
          <div className="relative flex-shrink-0" ref={sortRef}>
            <button 
              onClick={() => setIsSortOpen(!isSortOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="hidden md:inline">Sort: {filters.sort_by === 'smart_sort' ? 'Recommended' : filters.sort_by === 'collaborations' ? 'Most Collaborations' : filters.sort_by === 'distance' ? 'Nearest' : filters.sort_by === 'hourly_rate' ? (filters.sort_order === 'asc' ? 'Price: Low to High' : 'Price: High to Low') : filters.sort_by === 'rating' ? 'Highest Rated' : 'Newest'}</span>
              <ChevronUpDownIcon className="w-4 h-4 text-gray-400 hidden md:block" />
              <ArrowsUpDownIcon className="w-4 h-4 text-gray-700 dark:text-gray-200 md:hidden" />
            </button>
            
            {isSortOpen && (
              <>
                {/* Mobile Backdrop */}
                <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setIsSortOpen(false)} />
                
                {/* Dropdown / Bottom Sheet */}
                <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-2xl border-t border-gray-200 dark:border-gray-700 shadow-2xl z-50 md:absolute md:top-full md:left-0 md:right-auto md:bottom-auto md:w-56 md:rounded-xl md:border md:shadow-xl py-2 md:py-1 max-h-[80vh] overflow-y-auto">
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 md:hidden flex justify-between items-center">
                    <span className="font-bold text-gray-900 dark:text-white">Sort By</span>
                    <button onClick={() => setIsSortOpen(false)} className="p-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                      <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      onFilterChange('sort_by', 'smart_sort');
                      setIsSortOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 md:py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${filters.sort_by === 'smart_sort' ? 'text-black dark:text-white font-bold bg-gray-50 dark:bg-gray-700' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Recommended
                  </button>
                  <button
                    onClick={() => {
                      onFilterChange('sort_by', 'collaborations');
                      setIsSortOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 md:py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${filters.sort_by === 'collaborations' ? 'text-black dark:text-white font-bold bg-gray-50 dark:bg-gray-700' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Most Collaborations
                  </button>
                  <button
                    onClick={() => {
                      onFilterChange('sort_by', 'distance');
                      setIsSortOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 md:py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${filters.sort_by === 'distance' ? 'text-black dark:text-white font-bold bg-gray-50 dark:bg-gray-700' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Nearest
                  </button>
                  <button
                    onClick={() => {
                      onFilterChange('sort_by', 'hourly_rate');
                      onFilterChange('sort_order', 'asc');
                      setIsSortOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 md:py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${filters.sort_by === 'hourly_rate' && filters.sort_order === 'asc' ? 'text-black dark:text-white font-bold bg-gray-50 dark:bg-gray-700' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Price: Low to High
                  </button>
                  <button
                    onClick={() => {
                      onFilterChange('sort_by', 'hourly_rate');
                      onFilterChange('sort_order', 'desc');
                      setIsSortOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 md:py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${filters.sort_by === 'hourly_rate' && filters.sort_order === 'desc' ? 'text-black dark:text-white font-bold bg-gray-50 dark:bg-gray-700' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Price: High to Low
                  </button>
                  <button
                    onClick={() => {
                      onFilterChange('sort_by', 'created_at');
                      setIsSortOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 md:py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${filters.sort_by === 'created_at' ? 'text-black dark:text-white font-bold bg-gray-50 dark:bg-gray-700' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Newest First
                  </button>
                  <button
                    onClick={() => {
                      onFilterChange('sort_by', 'rating');
                      setIsSortOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 md:py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${filters.sort_by === 'rating' ? 'text-black dark:text-white font-bold bg-gray-50 dark:bg-gray-700' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Highest Rated
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="w-px h-6 bg-gray-200 mx-2 flex-shrink-0" />

          {/* Quick Filter: User Type */}
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-full p-0.5 flex-shrink-0">
            <button
              onClick={() => onFilterChange('user_type', undefined)}
              className={`px-2.5 py-1 rounded-full text-[10px] md:text-xs font-medium transition-all ${
                !filters.user_type
                  ? 'bg-white dark:bg-gray-600 text-black dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              All
            </button>
            {userTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => onFilterChange('user_type', type.value)}
                className={`px-2.5 py-1 rounded-full text-[10px] md:text-xs font-medium transition-all ${
                  filters.user_type === type.value
                    ? 'bg-white dark:bg-gray-600 text-black dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>

          {/* Quick Filter: Availability */}
          <button
            onClick={() => onFilterChange('is_available', filters.is_available ? undefined : true)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${
              filters.is_available
                ? 'bg-green-500 border-green-500 text-white'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Available Now
          </button>

          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <button
              onClick={onClearFilters}
              className="ml-auto flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 px-3 py-2 transition-colors flex-shrink-0"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear all
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
